import type { Dispatch, SetStateAction} from 'react';
import { useMemo } from 'react';

import { clamp } from 'lodash';

interface OffsetPaginationParams {
  totalItems: number;
  itemsPerPage: number;
  offset: number;
  setOffset: Dispatch<SetStateAction<number>>;
}

const useOffsetPagination = ({
  totalItems,
  itemsPerPage,
  offset,
  setOffset,
}: OffsetPaginationParams) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const currentPage = Math.floor(offset / itemsPerPage) + 1;
  const isLastPage = currentPage >= totalPages;
  const isFirstPage = currentPage <= 1;
  const controller = useMemo(
    () => ({
      prev: () => setOffset((oldState) => Math.max(oldState - itemsPerPage, 0)),
      next: () => setOffset((oldState) => Math.min(oldState + itemsPerPage, totalItems)),
      reset: () => setOffset(0),
      goToPage: (page: number) => setOffset(clamp(page * itemsPerPage, 0, totalItems)),
    }),
    [itemsPerPage, totalItems],
  );

  return {
    totalPages,
    currentPage,
    isFirstPage,
    isLastPage,
    ...controller,
  };
};

export default useOffsetPagination;
